import React from "react";
import { RouteComponentProps } from "react-router";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Row, Col, Button, Modal, message, Table, Spin } from "antd";
import Card from "../components/Card";
import { User, APIError } from "../helpers/shared-interfaces";
import { request, handleValidationErrors } from "../helpers/utilities";
import { AxiosResponse } from "axios";
import EntitledSection from "../components/EntitledSection";
import entitlements from "../config/entitlements";
import * as Sentry from "@sentry/browser";
import BaseForm from "../components/Form/BaseForm";
import { FormInstance ,} from "antd/lib/form";
import ModalCreator from "../components/ModalCreator";
import { UPDATE_DEPARTMENT } from "../gql/departments";
import { generateDisplayErrorMessage } from "../helpers/utilities";


interface State {
  name: string;
  isLoading: boolean;
  hasChanged: boolean;
  id: string | number;
  showDialog: boolean;
  users: User[];
  showUpdateDepartmentModal: boolean;
  isUpdatingDepartment: boolean;
}

interface RouteParams {
  id: string;
}

const columns = [
  {
    title: "Users",
    dataIndex: "name",
    key: "Name",
  },
];

export const DELETE_DEPARTMENT_MUTATION = `
  mutation DeleteDepartment($id:ID!) {
    deleteDepartment(id: $id ){
      result
      errors {
        property
        description
      }
    }
  }
`;

export const GET_DEPARTMENT_QUERY = `
  query GetDepartment($id:ID!) {
    department(id: $id ){
      id
      name
      users {
        id
        name
        email
      }
    }
  }
`;

export function injectKeyProp(data: User[]): User[] {
  const tempData: User[] = data?.map((user: User): User => {
    return { ...user, key: user.id };
  });
  return tempData;
}

function ModalWrapper(
  showDialog: boolean,
  deleteDepartment: () => void,
  toggleDeleteConfirmationModal: () => void,
): JSX.Element {
  return (
    <Modal
      title="Confirm Deletion Action"
      visible={showDialog}
      {...{
        okText: "Yes",
        okType: "danger",
        cancelButtonProps: {
          className: "modal-cancel-btn",
        },
        okButtonProps: {
          className: "modal-confirm-btn",
        },
      }}
      onOk={deleteDepartment}
      onCancel={toggleDeleteConfirmationModal}
    >
      <div>Are you sure you want to delete this department?</div>
    </Modal>
  );
}

export default class Department extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  reference: React.RefObject<FormInstance>;
  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      users: [],
      isLoading: true,
      id: "",
      name: "",
      hasChanged: false,
      showDialog: false,
      showUpdateDepartmentModal: false,
      isUpdatingDepartment: false,
    };
    this.reference = React.createRef<FormInstance>();
    this.deleteDepartment = this.deleteDepartment.bind(this);
    this.toggleDeleteConfirmationModal =
      this.toggleDeleteConfirmationModal.bind(this);
    this.getDepartmentData = this.getDepartmentData.bind(this);
  }

  toggleDeleteConfirmationModal(): void {
    const prevState = this.state.showDialog;
    this.setState({ showDialog: !prevState });
  }

  redirectToAllDepartmentsPage = (): void => {
    this.props.history.push("/departments");
  };

  getDepartmentData(): void {
    const { id } = this.state;
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: GET_DEPARTMENT_QUERY,
          variables: { id },
        }).then(({ data }: AxiosResponse<any>): void => {
          this.setState({ isLoading: false });
          if (!data.errors) {
            this.setState({
              name: data.data.department?.name,
              users: data.data.department?.users,
            });
          } else {
            message.error("Failed to Get department data");
          }
        });
      },
    );
  }

  handleInputChange(
    e: React.ChangeEvent<{
      value: string;
    }>,
  ): void {
    this.setState({ name: e.target.value });
  }

  toggleUpdateDepartmentModal = (): void => {
    this.setState(
      (prevState) => ({
        showUpdateDepartmentModal: !prevState.showUpdateDepartmentModal,
      }),
      () => {
        if (this.state.showUpdateDepartmentModal) {
        } else {
          this.reference.current?.resetFields();
        }
      },
    );
  };

  async deleteDepartment(): Promise<void> {
    interface Response {
      data: {
        deleteDepartment: {
          result: boolean;
        };
      };
    }
    const { id } = this.state;

    this.setState({ isLoading: true });

    try {
      const { data }: AxiosResponse<Response> = await request({
        query: DELETE_DEPARTMENT_MUTATION,
        variables: { id },
      });

      this.setState({ isLoading: false, showDialog: false });

      if (data.data.deleteDepartment.result) {
        message.success("Department deleted successfuly");
        this.redirectToAllDepartmentsPage();
      } else {
        message.error("Failed to delete department");
      }
    } catch (error) {
      Sentry.captureException(error);
      message.error(`Request Failed. - ${generateDisplayErrorMessage(error)}`);
    }
  }
   updateDepartment = async (): Promise<void> => {
      interface Response {
        data?: {
          updateDepartment?: {
            department?: {
              name: string;
            };
            errors?: APIError[];
          };
        };
        errors?: Record<string, unknown>[];
      }
  
      if (!this.reference.current) return;
      
      let data;
  
      try {
        data = await this.reference.current.validateFields(["name"]);
      } catch (error) {
        handleValidationErrors(error);
        return;
      }
  
      try {
        const response: AxiosResponse<Response> = await request({
          query: UPDATE_DEPARTMENT,
          variables: { name: data.name, id: this.state.id },
        });
        if (
          response &&
          response.data &&
          response.data.data &&
          response.data.data.updateDepartment &&
          response.data.data.updateDepartment.department
        ) {
          message.success("Department updated sucessfully");
          this.setState(
          {
            showUpdateDepartmentModal: false,
          },
          this.getDepartmentData,
        );
        } else {
          message.error("Failed to update department");
        }
      } catch (error) {
        message.error(`Failed to update department - ${generateDisplayErrorMessage(error)}`);
        Sentry.captureException(error);
      }
      finally {
        this.setState({ isUpdatingDepartment: false });
      }
    };

  componentDidMount(): void {
    this.setState({ id: this.props.match.params.id }, (): void => {
      this.getDepartmentData();
    });
  }

  render(): JSX.Element | null {
    const { isLoading, name, showDialog, users, showUpdateDepartmentModal,
    isUpdatingDepartment, } = this.state;
    return (
      <Spin spinning={isLoading}>
        <Card>
          <div>
            {ModalWrapper(
              showDialog,
              this.deleteDepartment,
              this.toggleDeleteConfirmationModal,
            )}
            <Row
              style={{
                marginBottom: "20px",
              }}
            >
              <Col
                style={{
                  textAlign: "left",
                }}
                span={12}
              >
                <div>Department Name</div>
                <div>
                  <h1
                    style={{
                      fontWeight: "bold",
                      fontSize: "25px",
                      marginBottom: "0px",
                    }}
                    data-cy="test-department-name"
                  >
                    {name?.toUpperCase()}
                  </h1>
                </div>
              </Col>

              <Col
                style={{
                  textAlign: "right",
                }}
                span={12}
              >
                <EntitledSection entitlement={entitlements.Departments.edit}>
                    <Button
                      icon={<EditOutlined />}
                       onClick={this.toggleUpdateDepartmentModal}
                      size="large"
                      style={{
                        backgroundColor: "  rgb(29, 55, 100)",
                        color: "white",
                        marginRight: "10px",
                      }}
                      data-cy="test-edit-department-button"
                    >
                      Edit
                    </Button>
                </EntitledSection>
                {users?.length === 0 && (
                  <EntitledSection
                    entitlement={entitlements.Departments.delete}
                  >
                    <Button
                      icon={<DeleteOutlined />}
                      size="large"
                      onClick={this.toggleDeleteConfirmationModal}
                      data-cy="test-delete-department-button"
                    >
                      Delete
                    </Button>
                  </EntitledSection>
                )}
              </Col>
            </Row>

            <Row>
              <Table
                bordered={true}
                columns={columns}
                dataSource={injectKeyProp(users)}
              />
            </Row>
            {showUpdateDepartmentModal &&
              ModalCreator({
                title: "Update Department",
                okText: "Update",
                okType: "primary",
                showModal: showUpdateDepartmentModal,
                onOkay: this.updateDepartment,
                toggleModal: this.toggleUpdateDepartmentModal,
                inscription: (
                  <Spin spinning={isUpdatingDepartment}>
                    <BaseForm
                      layout="vertical"
                      reference={this.reference}
                      fieldsToHide={[]}
                      fields={[
                        {
                          name: "name",
                          label: "Name",
                          initialValue: name,
                          inputType: "string",
                          placeholder: "Enter name",
                          required: false,
                          colConfig: { span: 22 },
                        },
                      ]}
                    />
                  </Spin>
                ),
              })}
          </div>
        </Card>
      </Spin>
    );
  }
}
